import RequestService from './request';

export type ImporterDriver =
    | 'pterodactyl_login'
    | 'pterodactyl_subuser'
    | 'shockbyte_invite'
    | 'tcadmin'
    | 'generic_sftp'
    | 'generic_ftp'
    | 'nitrado';

/**
 * Stable identifier for built-in driver cards. The backend sends
 * these as `kind` on entries with `source === 'builtin'` so the
 * wizard can look up the right credentials component without
 * matching on the free-form driver name.
 */
export type ImporterBuiltinKind = 'sftp' | 'ftp' | 'pterodactyl-subuser' | 'tcadmin-generic';

export interface ImporterAuthField {
    name: string;
    type: 'text' | 'url' | 'email' | 'password' | 'number';
    label_key: string;
    /** i18n key for the placeholder; consumers should pass it through `t()`. */
    placeholder_key?: string;
    required?: boolean;
}

export interface ImporterHost {
    slug: string;
    name: string;
    driver: ImporterDriver;
    /**
     * 'host'    — curated branded provider row. Ranked by how often
     *             servers on this egg have started an import from it
     *             (then global import popularity, then catalog order).
     * 'builtin' — generic driver appended to the end of the list.
     *             Creds are entered inline per-import and never persisted.
     */
    source: 'host' | 'builtin';
    logo_url: string | null;
    video_url: string | null;
    guide_url: string | null;
    aliases: string[];
    hints: Record<string, string>;
    locked_fields: string[];
    auth_fields: ImporterAuthField[];
    // Builtin-only fields. Optional on the shared type so existing
    // call sites don't need exhaustive narrowing — the wizard's
    // builtin branch is the only place that reads them.
    /** Stable id used by the wizard to route to the right creds form. */
    kind?: ImporterBuiltinKind;
    /** FontAwesome solid icon key used in place of `logo_url`. */
    icon?: string | null;
    /** Same schema as `auth_fields`; kept under its backend name for clarity. */
    credential_fields?: ImporterAuthField[];
    /** true ⇒ wizard skips StartupMapper and goes files → review. */
    skips_startup?: boolean;
    /** true ⇒ import streams files directly without source-side compression. */
    supports_file_only?: boolean;
}

export interface ImporterCatalog {
    hosts: ImporterHost[];
    /** Per-egg default selections used by the file picker. */
    eggDefaults: Record<string, string[]>;
    /**
     * Email domain the subuser driver mints invite aliases under —
     * derived panel-side from the configured catch-all mailbox.
     * Wizard forwards it to the daemon at login time so only the
     * panel holds mailbox config.
     */
    aliasDomain: string;
}

/*
 * Both endpoints take an explicit `serverId` rather than relying on
 * the request service's `:server` placeholder substitution. The
 * substitution reads from the router's current route params, which
 * isn't reliably populated when these are called from an onMounted
 * hook during the route transition.
 */
class ImporterHostsService {
    async getCatalog(serverId: string): Promise<ImporterCatalog> {
        const res = await RequestService.get(`/servers/${encodeURIComponent(serverId)}/importer/hosts`).then(
            (response: {
                data: ImporterHost[];
                egg_defaults?: Record<string, string[]>;
                alias_domain?: string;
            }) => response,
        );
        return {
            hosts: res.data,
            eggDefaults: res.egg_defaults ?? {},
            aliasDomain: res.alias_domain ?? '',
        };
    }

    /**
     * Clear the panel's import lock for a server. Recovery path for a
     * lock that stuck because the daemon died mid-import before it
     * could report a terminal status -- the websocket `cancel` op
     * gracefully aborts a live run, this just frees the panel-side UI.
     */
    abort(serverId: string): Promise<void> {
        return RequestService.post(`/servers/${encodeURIComponent(serverId)}/importer/abort`);
    }
}

export default new ImporterHostsService();
